import React, { useEffect, useMemo, useRef, useState } from "react";
import "../styles/shared-ui.css";
import "../styles/function-management.css";
import { useAppContext } from "../context/AppContext";
import {
  getFunctionById,
  getFunctionsByCompany,
  patchFunction,
} from "../services/functionService";
import { getCompanyNames } from "../services/companyService";
import { toast } from "../utils/toast";
import RichTextEditor from "../components/ui/RichTextEditor";
import ColorPicker from "../components/ui/ColorPicker";
import SideTabs from "../components/layout/SideTabs";
import { useLocation, useParams } from "react-router-dom";

const FunctionEditPage = () => {
  const { functionId, setFunctionId, setActiveSection } = useAppContext();
  const { functionId: functionIdParam } = useParams();
  const location = useLocation();
  const routeFunctionId = useMemo(() => {
    if (functionIdParam != null && functionIdParam !== "") {
      try {
        return decodeURIComponent(functionIdParam);
      } catch {
        return functionIdParam;
      }
    }
    const pathname = String(location?.pathname || "");
    const m = pathname.match(/^\/functions\/([^/]+)\/edit$/i);
    if (!m?.[1]) return null;
    try {
      return decodeURIComponent(m[1]);
    } catch {
      return m[1];
    }
  }, [functionIdParam, location?.pathname]);
  const [form, setForm] = useState({
    name: "",
    function_code: "",
    background_color: "#A3A3A3",
    company_id: "",
    parent_id: "",
    description: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [functions, setFunctions] = useState([]);

  // Helper to load functions for a specific company
  const loadFunctionsForCompany = async (companyId) => {
    if (!companyId) {
      setFunctions([]);
      return;
    }
    try {
      const fn = await getFunctionsByCompany(companyId).catch(() => []);
      setFunctions(Array.isArray(fn) ? fn : []);
    } catch {
      setFunctions([]);
    }
  };

  const didInit = useRef(false);
  useEffect(() => {
    (async () => {
      if (didInit.current) return; // StrictMode guard
      didInit.current = true;
      try {
        setLoading(true);
        const companiesRes = await getCompanyNames().catch(() => []);
        setCompanies(Array.isArray(companiesRes) ? companiesRes : []);

        let fid = routeFunctionId || functionId;
        if (routeFunctionId) {
          setFunctionId(routeFunctionId);
          try {
            localStorage.setItem("activeFunctionId", String(routeFunctionId));
          } catch {}
        }
        if (!fid) {
          try {
            const stored = localStorage.getItem("activeFunctionId");
            if (stored) {
              fid = stored;
              setFunctionId(stored);
            }
          } catch {}
        }
        if (!fid) {
          toast.error("No function ID provided");
          setActiveSection("function-management");
          return;
        }

        const f = await getFunctionById(fid);
        // Load functions for the function's company
        const funcCompanyId =
          f?.company_id ||
          companiesRes?.[0]?.company_id ||
          companiesRes?.[0]?.id ||
          "";
        if (funcCompanyId) {
          await loadFunctionsForCompany(funcCompanyId);
        }
        setForm({
          name: f?.name || "",
          function_code: f?.function_code || "",
          background_color: f?.background_color || "#A3A3A3",
          company_id:
            f?.company_id ||
            companiesRes?.[0]?.company_id ||
            companiesRes?.[0]?.id ||
            "",
          parent_id:
            f?.parent_id && String(f.parent_id) !== String(fid) ?
              f.parent_id
            : "",
          description: f?.description || "",
        });
      } catch (e) {
        toast.error(e?.message || "Failed to load function");
      } finally {
        setLoading(false);
      }
    })();
  }, [functionId, routeFunctionId, setFunctionId, setActiveSection]);

  const canSave = useMemo(
    () =>
      Boolean(
        String(form.name).trim() &&
          String(form.function_code).trim() &&
          String(form.company_id),
      ),
    [form.name, form.function_code, form.company_id],
  );

  const goBack = () => setActiveSection("function-management");
  const save = async () => {
    if (!canSave) {
      toast.error("Function Name, Function Code, and Company are required");
      return;
    }
    try {
      setSaving(true);
      const targetId =
        routeFunctionId ||
        functionId ||
        (() => {
          try {
            return localStorage.getItem("activeFunctionId");
          } catch {
            return null;
          }
        })();
      if (!targetId) {
        toast.error("Unable to determine function ID");
        return;
      }
      await patchFunction(targetId, form);
      toast.success(
        `Function "${form.name || "Untitled Function"}" edited successfully!`,
      );
      try {
        localStorage.setItem("functionsNeedsRefresh", "1");
      } catch {}
      setActiveSection("function-management");
    } catch (e) {
      toast.error(e?.message || "Failed to save function");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Edit Function</h2>
            <p className="page-subtitle">
              {form.name ? `Editing: ${form.name}` : "Update function details"}
              {form.function_code ? ` (${form.function_code})` : ""}
            </p>
          </div>
          <div className="roles-toolbar">
            <button className="secondary-btn" onClick={goBack}>
              Cancel
            </button>
            <button
              className="primary-btn"
              onClick={save}
              disabled={saving || !canSave}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        {loading ?
          <div className="no-results">Loading...</div>
        : <SideTabs
            defaultActiveId="basic"
            tabs={[
              {
                id: "basic",
                label: "Basic Details",
                content: (
                  <div className="role-card">
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Function Name *</label>
                        <input
                          value={form.name}
                          onChange={(e) =>
                            setForm((s) => ({ ...s, name: e.target.value }))
                          }
                        />
                      </div>
                      <ColorPicker
                        label="Background Color"
                        value={form.background_color}
                        onChange={(hex) =>
                          setForm((s) => ({ ...s, background_color: hex }))
                        }
                      />
                      <div className="form-group">
                        <label>Function Code *</label>
                        <input
                          value={form.function_code}
                          onChange={(e) =>
                            setForm((s) => ({
                              ...s,
                              function_code: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label>Select Company *</label>
                        <select
                          value={form.company_id}
                          onChange={(e) => {
                            const val = e.target.value;
                            setForm((s) => ({
                              ...s,
                              company_id: val,
                              parent_id: "",
                            }));
                            loadFunctionsForCompany(val);
                          }}
                        >
                          {companies.map((c) => (
                            <option
                              key={c.company_id || c.id}
                              value={c.company_id || c.id}
                            >
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Parent Function</label>
                        <select
                          value={form.parent_id}
                          onChange={(e) =>
                            setForm((s) => ({
                              ...s,
                              parent_id: e.target.value,
                            }))
                          }
                        >
                          <option value="">None</option>
                          {functions
                            .filter(
                              (f) =>
                                String(f.function_id) !==
                                String(routeFunctionId || functionId),
                            )
                            .map((f) => (
                              <option key={f.function_id} value={f.function_id}>
                                {f.name} 
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                id: "description",
                label: "Description",
                content: (
                  <div className="role-card">
                    <RichTextEditor
                      label="Function Overview"
                      value={form.description}
                      onChange={(html) =>
                        setForm((s) => ({ ...s, description: html }))
                      }
                      height={260}
                    />
                  </div>
                ),
              },
            ]}
          />
        }
      </div>
    </div>
  );
};

export default FunctionEditPage;
